import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Play,
  Pause,
  Settings,
  Target,
  Globe,
  Building2,
  Users,
  TrendingUp,
  Calendar,
  RefreshCw,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles,
  Filter,
  Download,
  MoreVertical,
  Truck,
  Package,
  Snowflake,
  MapPin,
  Ship,
  Warehouse,
  ShoppingCart,
  Factory,
  Store,
  Heart,
  Coffee,
  Car,
  Cpu } from
'lucide-react';

const API_URL = API_BASE || (
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:8001'
    : ''
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// Industry icons mapping
const INDUSTRY_ICONS = {
  logistics: Truck,
  '3pl': Package,
  cold_chain: Snowflake,
  last_mile: MapPin,
  freight: Ship,
  warehousing: Warehouse,
  ecommerce: ShoppingCart,
  manufacturing: Factory,
  retail: Store,
  healthcare: Heart,
  food_beverage: Coffee,
  automotive: Car,
  technology: Cpu,
  other: Building2
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  active: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400',
  paused: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400'
};

const validationStatusColors = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  approved: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400',
  needs_review: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400'
};

export const ProspectDiscovery = () => {const { t } = useTranslation("crm");
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [stagedProspects, setStagedProspects] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewCampaignDialog, setShowNewCampaignDialog] = useState(false);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // New campaign form state
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    use_google_search: true,
    use_directories: true,
    use_custom_urls: false,
    custom_seed_urls: [],
    search_keywords: [],
    target_industries: [],
    target_locations: [],
    ai_provider: 'openai',
    enrichment_enabled: true,
    max_prospects_per_run: 100
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [customUrlInput, setCustomUrlInput] = useState('');

  useEffect(() => {
    fetchData();
    fetchIndustryOptions();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, statsRes, prospectsRes] = await Promise.all([
      fetch(`${API_URL}/api/prospect-discovery/campaigns`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/prospect-discovery/stats`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/prospect-discovery/staged?limit=50`, { headers: getAuthHeaders() })]
      );

      if (campaignsRes.ok) setCampaigns(await campaignsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (prospectsRes.ok) {
        const data = await prospectsRes.json();
        setStagedProspects(data.items || []);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchIndustryOptions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/prospect-discovery/options/industries`, { headers: getAuthHeaders() });
      if (res.ok) setIndustryOptions(await res.json());
    } catch (error) {
      console.error('Failed to fetch industry options');
    }
  };

  const createCampaign = async () => {
    try {
      const res = await fetch(`${API_URL}/api/prospect-discovery/campaigns`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newCampaign)
      });

      if (!res.ok) throw new Error('Failed to create campaign');

      toast.success('Campaign created successfully');
      setShowNewCampaignDialog(false);
      setNewCampaign({
        name: '',
        description: '',
        use_google_search: true,
        use_directories: true,
        use_custom_urls: false,
        custom_seed_urls: [],
        search_keywords: [],
        target_industries: [],
        target_locations: [],
        ai_provider: 'openai',
        enrichment_enabled: true,
        max_prospects_per_run: 100
      });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const runCampaign = async (campaignId) => {
    try {
      const res = await fetch(`${API_URL}/api/prospect-discovery/campaigns/${campaignId}/run`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!res.ok) throw new Error('Failed to start campaign');

      toast.success('Discovery run started');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const validateProspect = async (prospectId, decision) => {
    try {
      const res = await fetch(`${API_URL}/api/prospect-discovery/staged/${prospectId}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ decision })
      });

      if (!res.ok) throw new Error('Failed to validate prospect');

      toast.success(`Prospect ${decision}`);
      fetchData();
      setSelectedProspect(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !newCampaign.search_keywords.includes(keywordInput.trim())) {
      setNewCampaign({
        ...newCampaign,
        search_keywords: [...newCampaign.search_keywords, keywordInput.trim()]
      });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setNewCampaign({
      ...newCampaign,
      search_keywords: newCampaign.search_keywords.filter((k) => k !== keyword)
    });
  };

  const addLocation = () => {
    if (locationInput.trim() && !newCampaign.target_locations.includes(locationInput.trim())) {
      setNewCampaign({
        ...newCampaign,
        target_locations: [...newCampaign.target_locations, locationInput.trim()]
      });
      setLocationInput('');
    }
  };

  const toggleIndustry = (industryValue) => {
    const current = newCampaign.target_industries;
    if (current.includes(industryValue)) {
      setNewCampaign({
        ...newCampaign,
        target_industries: current.filter((i) => i !== industryValue)
      });
    } else {
      setNewCampaign({
        ...newCampaign,
        target_industries: [...current, industryValue]
      });
    }
  };

  const filteredProspects = stagedProspects.filter((p) =>
  statusFilter === 'all' || p.validation_status === statusFilter
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="prospect-discovery">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("prospectDiscovery", "Prospect Discovery")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("automatedWebScraping", "Automated web scraping and AI-powered prospect enrichment")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button
            className="gap-2 bg-teal-600 hover:bg-teal-700"
            onClick={() => setShowNewCampaignDialog(true)}>
            
            <Plus className="h-4 w-4" />{t("newCampaign", "New Campaign")}

          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10">
                <Target className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalCampaigns", "Total Campaigns")}</p>
                <p className="text-2xl font-bold">{stats.total_campaigns || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("pendingValidation", "Pending Validation")}</p>
                <p className="text-2xl font-bold">{stats.pending_prospects || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("approved", "Approved")}</p>
                <p className="text-2xl font-bold">{stats.approved_prospects || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Sparkles className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("avgFitScore", "Avg Fit Score")}</p>
                <p className="text-2xl font-bold">{stats.avg_fit_score || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:w-[500px]">
          <TabsTrigger value="campaigns" className="gap-2">
            <Target className="h-4 w-4" />{t("campaigns", "Campaigns")}

          </TabsTrigger>
          <TabsTrigger value="validation" className="gap-2">
            <Users className="h-4 w-4" />{t("validationQueue", "Validation Queue")}

            {stats.pending_prospects > 0 &&
            <Badge className="ml-1 bg-amber-500">{stats.pending_prospects}</Badge>
            }
          </TabsTrigger>
          <TabsTrigger value="exclusions" className="gap-2">
            <XCircle className="h-4 w-4" />{t("exclusions", "Exclusions")}

          </TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          {campaigns.length === 0 ?
          <Card className="card-elevated">
              <CardContent className="py-12 text-center">
                <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t("noDiscoveryCampaigns", "No Discovery Campaigns")}</h3>
                <p className="text-muted-foreground mb-4">{t("createYourFirst", "Create your first campaign to start discovering prospects")}</p>
                <Button
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => setShowNewCampaignDialog(true)}>
                
                  <Plus className="mr-2 h-4 w-4" />{t("createCampaign", "Create Campaign")}

              </Button>
              </CardContent>
            </Card> :

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {campaigns.map((campaign) =>
            <Card key={campaign.id} className="card-elevated hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{campaign.name}</CardTitle>
                        <CardDescription className="mt-1 line-clamp-2">
                          {campaign.description || 'No description'}
                        </CardDescription>
                      </div>
                      <Badge className={statusColors[campaign.status]}>
                        {campaign.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Data Sources */}
                    <div className="flex gap-2">
                      {campaign.use_google_search &&
                  <Badge variant="outline" className="text-xs">
                          <Globe className="h-3 w-3 mr-1" />{t("google", "Google")}

                  </Badge>
                  }
                      {campaign.use_directories &&
                  <Badge variant="outline" className="text-xs">
                          <Building2 className="h-3 w-3 mr-1" />{t("directories", "Directories")}

                  </Badge>
                  }
                      {campaign.use_custom_urls &&
                  <Badge variant="outline" className="text-xs">
                          <Settings className="h-3 w-3 mr-1" />{t("custom", "Custom")}

                  </Badge>
                  }
                    </div>

                    {/* Industries */}
                    <div className="flex flex-wrap gap-1">
                      {(campaign.target_industries || []).slice(0, 3).map((ind) => {
                    const Icon = INDUSTRY_ICONS[ind] || Building2;
                    return (
                      <Badge key={ind} variant="secondary" className="text-xs">
                            <Icon className="h-3 w-3 mr-1" />
                            {ind}
                          </Badge>);

                  })}
                      {(campaign.target_industries || []).length > 3 &&
                  <Badge variant="secondary" className="text-xs">
                          +{campaign.target_industries.length - 3}
                        </Badge>
                  }
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t">
                      <div>
                        <p className="text-lg font-bold">{campaign.total_prospects || 0}</p>
                        <p className="text-xs text-muted-foreground">{t("found", "Found")}</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-amber-600">{campaign.pending_prospects || 0}</p>
                        <p className="text-xs text-muted-foreground">{t("pending", "Pending")}</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-green-600">{campaign.approved_prospects || 0}</p>
                        <p className="text-xs text-muted-foreground">{t("approved", "Approved")}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button
                    size="sm"
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                    onClick={() => runCampaign(campaign.id)}>
                    
                        <Play className="h-4 w-4 mr-1" />{t("runNow", "Run Now")}

                  </Button>
                      <Button size="sm" variant="outline">
                        <Settings className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
            )}
            </div>
          }
        </TabsContent>

        {/* Validation Queue Tab */}
        <TabsContent value="validation" className="space-y-4">
          {/* Filters */}
          <Card className="card-elevated">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search prospects..." className="pl-10" />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {['all', 'pending', 'approved', 'rejected'].map((status) =>
                  <Button
                    key={status}
                    variant={statusFilter === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter(status)}
                    className={statusFilter === status ? 'bg-teal-600 hover:bg-teal-700' : ''}>
                    
                      {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Prospects Table */}
          <Card className="card-elevated">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("company", "Company")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("industry", "Industry")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("fitScore", "Fit Score")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("completeness", "Completeness")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProspects.map((prospect) => {
                      const IndustryIcon = INDUSTRY_ICONS[prospect.industry] || Building2;
                      return (
                        <tr key={prospect.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-teal-500/20 to-blue-500/20 flex items-center justify-center">
                                <Building2 className="h-5 w-5 text-teal-600" />
                              </div>
                              <div>
                                <p className="font-medium">{prospect.company_name}</p>
                                <p className="text-xs text-muted-foreground">{prospect.domain}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <IndustryIcon className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{prospect.industry || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-bold ${
                            prospect.ai_fit_score >= 80 ? 'bg-green-100 text-green-700' :
                            prospect.ai_fit_score >= 60 ? 'bg-teal-100 text-teal-700' :
                            prospect.ai_fit_score >= 40 ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-700'}`
                            }>
                              {prospect.ai_fit_score || '-'}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="w-full bg-muted rounded-full h-2">
                              <div
                                className="bg-teal-500 h-2 rounded-full"
                                style={{ width: `${prospect.completeness_score || 0}%` }} />
                              
                            </div>
                            <span className="text-xs text-muted-foreground">{prospect.completeness_score || 0}%</span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge className={validationStatusColors[prospect.validation_status]}>
                              {prospect.validation_status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-1">
                              {prospect.validation_status === 'pending' &&
                              <>
                                  <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-green-600 hover:bg-green-100"
                                  onClick={() => validateProspect(prospect.id, 'approved')}>
                                  
                                    <CheckCircle2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                                  onClick={() => validateProspect(prospect.id, 'rejected')}>
                                  
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                </>
                              }
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0"
                                onClick={() => setSelectedProspect(prospect)}>
                                
                                <ArrowRight className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>);

                    })}
                  </tbody>
                </table>
              </div>

              {filteredProspects.length === 0 &&
              <div className="py-12 text-center">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t("noProspectsFound", "No prospects found")}</h3>
                  <p className="text-muted-foreground">{t("runADiscovery", "Run a discovery campaign to find new prospects")}</p>
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exclusions Tab */}
        <TabsContent value="exclusions">
          <Card className="card-elevated">
            <CardContent className="py-12 text-center">
              <XCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("exclusionRules", "Exclusion Rules")}</h3>
              <p className="text-muted-foreground mb-4">{t("manageDomainsAnd", "Manage domains and companies to exclude from future discovery")}

              </p>
              <Button variant="outline">
                <Plus className="mr-2 h-4 w-4" />{t("addExclusionRule", "Add Exclusion Rule")}

              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Campaign Dialog */}
      <Dialog open={showNewCampaignDialog} onOpenChange={setShowNewCampaignDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("createDiscoveryCampaign", "Create Discovery Campaign")}</DialogTitle>
            <DialogDescription>{t("configureYourAutomated", "Configure your automated prospect discovery settings")}

            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">{t("campaignName", "Campaign Name *")}</Label>
                <Input
                  id="name"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  placeholder="e.g., Q1 Logistics Prospects" />
                
              </div>
              <div>
                <Label htmlFor="description">{t("description", "Description")}</Label>
                <Input
                  id="description"
                  value={newCampaign.description}
                  onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                  placeholder="Brief description of the campaign goals" />
                
              </div>
            </div>

            {/* Data Sources */}
            <div className="space-y-3">
              <Label className="text-base">{t("dataSources", "Data Sources")}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  newCampaign.use_google_search ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' : 'border-border'}`
                  }
                  onClick={() => setNewCampaign({ ...newCampaign, use_google_search: !newCampaign.use_google_search })}>
                  
                  <Globe className="h-6 w-6 text-teal-600 mb-2" />
                  <p className="font-medium">{t("googleSearch", "Google Search")}</p>
                  <p className="text-xs text-muted-foreground">{t("searchEngineResults", "Search engine results")}</p>
                </div>
                <div
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  newCampaign.use_directories ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' : 'border-border'}`
                  }
                  onClick={() => setNewCampaign({ ...newCampaign, use_directories: !newCampaign.use_directories })}>
                  
                  <Building2 className="h-6 w-6 text-blue-600 mb-2" />
                  <p className="font-medium">{t("directories", "Directories")}</p>
                  <p className="text-xs text-muted-foreground">{t("businessDirectories", "Business directories")}</p>
                </div>
                <div
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  newCampaign.use_custom_urls ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' : 'border-border'}`
                  }
                  onClick={() => setNewCampaign({ ...newCampaign, use_custom_urls: !newCampaign.use_custom_urls })}>
                  
                  <Settings className="h-6 w-6 text-purple-600 mb-2" />
                  <p className="font-medium">{t("customUrls", "Custom URLs")}</p>
                  <p className="text-xs text-muted-foreground">{t("yourSeedUrls", "Your seed URLs")}</p>
                </div>
              </div>
            </div>

            {/* Keywords */}
            <div className="space-y-3">
              <Label>{t("searchKeywords", "Search Keywords")}</Label>
              <div className="flex gap-2">
                <Input
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                  placeholder="e.g., logistics company, 3PL provider" />
                
                <Button type="button" onClick={addKeyword} variant="outline">{t("add", "Add")}

                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {newCampaign.search_keywords.map((keyword) =>
                <Badge key={keyword} variant="secondary" className="gap-1">
                    {keyword}
                    <button onClick={() => removeKeyword(keyword)} className="ml-1 hover:text-destructive">×</button>
                  </Badge>
                )}
              </div>
            </div>

            {/* Target Industries */}
            <div className="space-y-3">
              <Label>{t("targetIndustries", "Target Industries")}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {industryOptions.map((industry) => {
                  const Icon = INDUSTRY_ICONS[industry.value] || Building2;
                  const isSelected = newCampaign.target_industries.includes(industry.value);
                  return (
                    <div
                      key={industry.value}
                      className={`p-2 rounded-lg border cursor-pointer transition-colors flex items-center gap-2 ${
                      isSelected ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' : 'border-border hover:border-muted-foreground'}`
                      }
                      onClick={() => toggleIndustry(industry.value)}>
                      
                      <Icon className={`h-4 w-4 ${isSelected ? 'text-teal-600' : 'text-muted-foreground'}`} />
                      <span className="text-sm truncate">{industry.label}</span>
                    </div>);

                })}
              </div>
            </div>

            {/* AI Provider */}
            <div className="space-y-3">
              <Label>{t("aiEnrichmentProvider", "AI Enrichment Provider")}</Label>
              <Select
                value={newCampaign.ai_provider}
                onValueChange={(value) => setNewCampaign({ ...newCampaign, ai_provider: value })}>
                
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="openai">{t("openaiGpt52", "OpenAI GPT-5.2")}</SelectItem>
                  <SelectItem value="claude">{t("claudeSonnet45", "Claude Sonnet 4.5")}</SelectItem>
                  <SelectItem value="gemini">{t("gemini3Flash", "Gemini 3 Flash")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Max Prospects */}
            <div className="space-y-3">
              <Label>{t("maxProspectsPer", "Max Prospects per Run")}</Label>
              <Input
                type="number"
                value={newCampaign.max_prospects_per_run}
                onChange={(e) => setNewCampaign({ ...newCampaign, max_prospects_per_run: parseInt(e.target.value) || 100 })}
                min={10}
                max={500} />
              
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCampaignDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700"
              onClick={createCampaign}
              disabled={!newCampaign.name}>{t("createCampaign", "Create Campaign")}


            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prospect Detail Dialog */}
      <Dialog open={!!selectedProspect} onOpenChange={() => setSelectedProspect(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedProspect &&
          <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-teal-500/20 to-blue-500/20 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <span>{selectedProspect.company_name}</span>
                    <p className="text-sm font-normal text-muted-foreground">{selectedProspect.domain}</p>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Scores */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-muted/30">
                    <p className="text-sm text-muted-foreground mb-1">{t("aiFitScore", "AI Fit Score")}</p>
                    <p className={`text-3xl font-bold ${
                  selectedProspect.ai_fit_score >= 70 ? 'text-green-600' :
                  selectedProspect.ai_fit_score >= 50 ? 'text-amber-600' : 'text-gray-600'}`
                  }>
                      {selectedProspect.ai_fit_score || 0}%
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/30">
                    <p className="text-sm text-muted-foreground mb-1">{t("completeness", "Completeness")}</p>
                    <p className="text-3xl font-bold text-teal-600">
                      {selectedProspect.completeness_score || 0}%
                    </p>
                  </div>
                </div>

                {/* AI Summary */}
                {selectedProspect.ai_summary &&
              <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      <span className="font-medium text-purple-700 dark:text-purple-300">{t("aiAnalysis", "AI Analysis")}</span>
                    </div>
                    <p className="text-sm">{selectedProspect.ai_summary}</p>
                  </div>
              }

                {/* Industry & Services */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">{t("industry", "Industry")}</Label>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(selectedProspect.industry_tags || []).map((tag) =>
                    <Badge key={tag} variant="secondary">{tag}</Badge>
                    )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">{t("techSignals", "Tech Signals")}</Label>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(selectedProspect.tech_signals || []).map((signal) =>
                    <Badge key={signal} variant="outline">{signal}</Badge>
                    )}
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-2">
                  <Label className="text-muted-foreground">{t("contactInformation", "Contact Information")}</Label>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t("email", "Email")}</p>
                      <p className="font-medium">{selectedProspect.company_email || 'Not available'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t("phone", "Phone")}</p>
                      <p className="font-medium">{selectedProspect.company_phone || 'Not available'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedProspect(null)}>{t("close", "Close")}

              </Button>
                {selectedProspect.validation_status === 'pending' &&
              <>
                    <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => validateProspect(selectedProspect.id, 'rejected')}>
                  
                      <XCircle className="h-4 w-4 mr-2" />{t("reject", "Reject")}

                </Button>
                    <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => validateProspect(selectedProspect.id, 'approved')}>
                  
                      <CheckCircle2 className="h-4 w-4 mr-2" />{t("approve", "Approve")}

                </Button>
                  </>
              }
              </DialogFooter>
            </>
          }
        </DialogContent>
      </Dialog>
    </div>);

};

export default ProspectDiscovery;
